import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';

import { ResultChip } from '@components/ResultChip';
import { SectionDivider } from '@components/SectionDivider';
import { formatCalibrationData } from '@pages/Work/DetailProfilePage/components/DetailHeader/utils/detailHeaderUtils';
import { formatDateTime } from '@utils/dateHelpers';
import { downloadCertificateFromApi } from '@utils/pdfDownload';
import { calculateOverallResults } from '@features/customerReview/utils/calibrationResultCalculators';

import type { CalibrationRuntimeData } from '@features/calibration/hooks/useCalibrationRuntime/types';
import type { DcRuntimeViewModel } from '@api/bff/openapi/models';
import type { WorkOrderDetailSummary } from '@api/bff/workOrders/useWorkOrderDetailByGlobalId';

type Props = {
  calibrationData: CalibrationRuntimeData;
  calibrationRuntime: DcRuntimeViewModel;
  workOrderDetail?: WorkOrderDetailSummary;
};

const HistoryHeader: React.FC<Props> = ({
  calibrationData,
  calibrationRuntime,
  workOrderDetail
}) => {
  const { t } = useTranslation('history');
  const theme = useTheme();

  const overallResults = useMemo(
    () => calculateOverallResults(calibrationData),
    [calibrationData]
  );

  const resultLabel = overallResults.asLeftPassed ? 'Pass' : 'Fail';
  const eventNumber = calibrationRuntime.eventNum;
  const calibrationDate = formatDateTime(calibrationRuntime.lastModified);
  const performedBy = calibrationRuntime.lastModifiedBy || '--';

  const equipmentId = workOrderDetail?.equipmentId || '--';
  const companyName = workOrderDetail?.companyName || '--';
  const jobNumber = workOrderDetail?.jobNumber;
  const description = workOrderDetail?.description;

  const environmentalString = useMemo(() => {
    return formatCalibrationData(calibrationData.environmental);
  }, [calibrationData.environmental]);

  return (
    <Paper
      elevation={1}
      sx={{
        p: { xs: 2, sm: 3 },
        width: '100%'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { sm: 'space-between' },
          alignItems: { sm: 'flex-start' },
          gap: 0.5,
          mb: 1
        }}
      >
        <Box>
          <Typography variant="h6" color="primary" fontWeight={600}>
            {equipmentId}
          </Typography>
          {jobNumber && (
            <Typography variant="body2" color="text.secondary">
              {t('header.workOrder')}: {jobNumber}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: { xs: 0.5, sm: 0 }
          }}
        >
          <ResultChip result={resultLabel} />
          {eventNumber != null && (
            <Chip
              icon={<CheckCircleIcon />}
              label={t('certificate.title')}
              variant="outlined"
              color="success"
              size="medium"
              deleteIcon={<DownloadIcon />}
              onDelete={() => downloadCertificateFromApi(eventNumber)}
              sx={{
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.success.main,
                  '&:hover': { color: theme.palette.success.dark }
                }
              }}
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 0.5, sm: 2 },
          mb: 1
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {companyName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {calibrationDate}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('performedBy')}: {performedBy}
        </Typography>
        {eventNumber != null && (
          <Typography variant="body2" color="text.secondary">
            {t('header.eventNumber')}: {eventNumber}
          </Typography>
        )}
      </Box>

      <SectionDivider spacing={0} sx={{ mb: 2, mt: 1 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {description && (
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 0.5, sm: 2 }
          }}
        >
          {environmentalString && (
            <Typography variant="body2" color="text.secondary">
              {environmentalString}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default HistoryHeader;
